import { useState, useCallback } from 'react';
import type { InstitutionCorrespondence } from '../../../types';

interface UseDraftEditDialogProps {
  allCorrespondence: InstitutionCorrespondence[];
  onUpdateDraft?: (draftId: string, content: string) => Promise<void>;
  onDeleteDraft?: (draftId: string) => Promise<void>;
}

/**
 * Manages dialog state for editing, saving, and deleting drafts.
 */
export function useDraftEditDialog({
  allCorrespondence,
  onUpdateDraft,
  onDeleteDraft
}: UseDraftEditDialogProps) {
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [editDraftContent, setEditDraftContent] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);

  const handleEditDraft = useCallback(
    (draftId: string) => {
      const draft = allCorrespondence.find((c) => String(c.id) === draftId);
      if (draft) {
        const content =
          draft.fragments?.mainContent || draft.body || draft.content || '';
        setEditDraftContent(content);
        setEditingDraftId(draftId);
        setShowEditDialog(true);
      }
    },
    [allCorrespondence]
  );

  const handleSaveEditedDraft = useCallback(
    async (content: string) => {
      if (editingDraftId && onUpdateDraft) {
        await onUpdateDraft(editingDraftId, content);
        setShowEditDialog(false);
        setEditingDraftId(null);
        setEditDraftContent('');
      }
    },
    [editingDraftId, onUpdateDraft]
  );

  const handleDraftDelete = useCallback(
    async (draftId: string) => {
      if (onDeleteDraft) {
        await onDeleteDraft(draftId);
      }
      setEditingDraftId(null);
    },
    [onDeleteDraft]
  );

  return {
    editingDraftId,
    showEditDialog,
    editDraftContent,
    setShowEditDialog,
    handleEditDraft,
    handleSaveEditedDraft,
    handleDraftDelete
  };
}
